import { isEmpty } from 'lodash';
import moment from 'moment';
import type { PriceOfDay, PricesByDays, ClosingDate } from 'Store/calendar/calendar.helper';
import { getClosingDatesRanges } from 'Store/calendar/calendar.helper';
import { API_DATE_FORMAT } from 'Utils/date/apiDate';
import { DEPARTURE_DATE_MAX_DATE } from 'Utils/faceting/constants';
export type { OpeningDate } from 'Store/calendar/calendar.helper';
export type PriceResolver = (dayId: number) => PriceOfDay;
type DateValidator = (arg0: Date) => boolean;
export const PRICE_COLOR_CLASS_NAMES = {
  DARK_GREY: 'text-black hocus:text-black-active',
  GREEN: 'text-green',
  GREEN_OPACITY_45: 'text-green bestPrice',
  GREY: 'text-grey hocus:text-black-active',
  ORANGE: 'text-orange',
  ORANGE_OPACITY_45: 'text-orange lastAvailability',
  WHITE: 'text-white',
  TURQUOISE: 'text-turquoise',
};
export const mixedToMoment = (
  mixedDate: (number | Date | moment$Moment) | null | undefined,
): moment$Moment => {
  return moment(mixedDate);
};
export const dateToInt = (d: Date, format: string = API_DATE_FORMAT) =>
  parseInt(mixedToMoment(d).format(format), 10);
export const dateDiff = (day1: Date | string, day2: Date | string) => {
  return Math.abs(moment(day2).diff(moment(day1), 'days'));
};
export const stringToDateFormatted = (date: Date | string, format: string) => {
  return moment(date).format(format);
};
export const momentToInt = (date: moment$Moment, format: string = API_DATE_FORMAT) =>
  parseInt(date.format(format), 10);
export const maxDatepickerDate = (): Date => moment().add(DEPARTURE_DATE_MAX_DATE).toDate();
export const minDatepickerDate = (offset: number | null | undefined = 1): Date =>
  moment()
    .add({
      days: offset,
    })
    .toDate();

function daysWithoutDepartureFactory(
  resolver: PriceResolver,
  fromDate: Date | null | undefined,
  toDate: Date | null | undefined,
): DateValidator {
  return (day) => {
    const { exclusivity = false, noFlight = false, full = false } = resolver(dateToInt(day));
    const available = !exclusivity && !noFlight && !full;

    if (!fromDate && !toDate) {
      return !available;
    }

    if (fromDate && !toDate) {
      return false;
    }

    const dayIsSelected =
      fromDate &&
      toDate &&
      dateToInt(day) >= dateToInt(fromDate) &&
      dateToInt(day) <= dateToInt(toDate);
    return !dayIsSelected && !available;
  };
}

const daysWithNoAvailabilityFactory = (resolver: PriceResolver): DateValidator => {
  return (day) => {
    const data = resolver(dateToInt(day));
    return isEmpty(data) || data.noAvailability;
  };
};

export const closedDaysFactory = (
  resolver: PriceResolver,
  fromDate: Date | null | undefined,
  toDate: Date | null | undefined,
  shouldAllowSelectingSiblingOpenDays: boolean,
): DateValidator => {
  return (day) => {
    const { closed = false, siblingOpen = false } = resolver(dateToInt(day));

    if (!fromDate && !toDate) {
      return closed || (siblingOpen && !shouldAllowSelectingSiblingOpenDays);
    }

    if (fromDate && !toDate) {
      return false;
    }

    const dayIsSelected =
      fromDate &&
      toDate &&
      dateToInt(day) >= dateToInt(fromDate) &&
      dateToInt(day) <= dateToInt(toDate);
    return !dayIsSelected && (closed || (siblingOpen && !shouldAllowSelectingSiblingOpenDays));
  };
};
export const firstSiblingOpenDayOfSelectedMonthFactory = (
  resolver: PriceResolver,
  selectedMonth?: number,
): DateValidator => {
  let firstSiblingOpenDayFound;
  return (day) => {
    if (!selectedMonth) {
      return false;
    }

    const firstDayOfSelectedMonth = `${selectedMonth.toString()}01`;
    const firstDayOfSelectedMonthAsDate = moment(firstDayOfSelectedMonth, API_DATE_FORMAT);
    const firstDisplayedDayOfSelectedMonthAsInt =
      firstDayOfSelectedMonthAsDate.weekday() > 0
        ? momentToInt(
            firstDayOfSelectedMonthAsDate.subtract(firstDayOfSelectedMonthAsDate.weekday(), 'days'),
          )
        : momentToInt(firstDayOfSelectedMonthAsDate);
    const lastDisplayedDayOfSelectedMonthAsInt = momentToInt(
      moment(firstDayOfSelectedMonthAsDate).add(41, 'days'),
    );
    const currentDayDateAsInt = dateToInt(day);

    if (
      (firstSiblingOpenDayFound && firstSiblingOpenDayFound !== dateToInt(day)) ||
      currentDayDateAsInt < firstDisplayedDayOfSelectedMonthAsInt ||
      currentDayDateAsInt > lastDisplayedDayOfSelectedMonthAsInt
    ) {
      return false;
    }

    const hasSiblingOpen = resolver(currentDayDateAsInt)?.siblingOpen || false;

    if (hasSiblingOpen) {
      firstSiblingOpenDayFound = dateToInt(day);
    }

    return hasSiblingOpen;
  };
};
export const siblingOpenDaysFactory = (resolver: PriceResolver): DateValidator => {
  return (day) => resolver(dateToInt(day))?.siblingOpen || false;
};

const unavailableReturnDaysFactory = (
  fromDate: Date,
  durations: Array<number> = [],
): DateValidator => {
  return (day) => {
    if (day < fromDate) {
      return false;
    }

    const duration = dateDiff(day, fromDate);

    // rule of max 30 days of reservation
    if (duration > 30) {
      return true;
    }

    return !!duration && !durations.includes(duration);
  };
};

type DisabledDaysArgs = {
  from: Date | null | undefined;
  isSeller: boolean;
  optionsDuration: Array<number>;
  prices: PricesByDays | null | undefined;
  to: Date | null | undefined;
  shouldAllowSelectingSiblingOpenDays: boolean;
  openingDates: Array<OpeningDate> | null | undefined;
  resolvePrice: PriceResolver;
};
export function getDisabledDays({
  from,
  isSeller,
  optionsDuration,
  openingDates,
  prices,
  resolvePrice,
  shouldAllowSelectingSiblingOpenDays,
  to,
}: DisabledDaysArgs): Array<DateValidator | ClosingDate> {
  const disabledDays = [];
  const pricesIds = Object.keys(prices || {});
  const hasPrices = pricesIds.length > 0;
  const allClosedDays = pricesIds
    .filter(
      (priceKey: string) =>
        prices &&
        (prices[priceKey].closed ||
          (prices[priceKey].siblingOpen && !shouldAllowSelectingSiblingOpenDays)),
    )
    .map((priceKey) => parseInt(priceKey, 10));

  if (hasPrices) {
    const dayIsWithoutDeparture = daysWithoutDepartureFactory(resolvePrice, from, to);
    const dayIsClosed = closedDaysFactory(
      resolvePrice,
      from,
      to,
      shouldAllowSelectingSiblingOpenDays,
    );
    const dayIsSiblingOpen = siblingOpenDaysFactory(resolvePrice);
    const dayIsWithoutData = daysWithNoAvailabilityFactory(resolvePrice);
    disabledDays.push(dayIsWithoutDeparture);
    disabledDays.push(dayIsClosed);

    if (!shouldAllowSelectingSiblingOpenDays) {
      disabledDays.push(dayIsSiblingOpen);
    }

    disabledDays.push(dayIsWithoutData);
  }

  if (from && !to) {
    const fromDay = dateToInt(from);
    const closedDaysAfterFromDay = allClosedDays.filter((closedDay) => closedDay > fromDay);
    const firstClosedDay =
      hasPrices &&
      closedDaysAfterFromDay.length > 0 &&
      Math.min.apply(null, closedDaysAfterFromDay);
    disabledDays.push({
      before: moment(from).toDate(),
      after: firstClosedDay
        ? moment(firstClosedDay.toString()).toDate()
        : moment(from).add(30, 'days').toDate(),
    });
  }

  if (from && !to && !isEmpty(optionsDuration) && !isSeller) {
    disabledDays.push(unavailableReturnDaysFactory(from, optionsDuration));
  }

  if (openingDates?.length) {
    disabledDays.push(...getClosingDatesRanges(openingDates));
  }

  return disabledDays;
}
type GetPriceColorArgs = {
  classColor: string;
  from: Date | null | undefined;
  hasPrices: boolean;
  resolvePrice: PriceResolver;
  selectedMonth?: number;
  to: Date | null | undefined;
};
export function getPriceColor({
  classColor,
  from,
  hasPrices,
  resolvePrice,
  selectedMonth,
  to,
}: GetPriceColorArgs) {
  return (day: Date) => {
    const dayAsInt = dateToInt(day);

    if (selectedMonth) {
      const firstDayOfCurrentMonth = Number.parseInt(`${selectedMonth}01`, 10);
      const lastDayOfCurrentMonth = Number.parseInt(`${selectedMonth}31`, 10);
      // Handle dates outside current month & without prices
      const isDayBeforeSelectedMonth = Boolean(dayAsInt < firstDayOfCurrentMonth);
      const isDayAfterSelectedMonth = Boolean(dayAsInt > lastDayOfCurrentMonth);

      if (isDayBeforeSelectedMonth || (!from && isDayAfterSelectedMonth) || !hasPrices) {
        return classColor === PRICE_COLOR_CLASS_NAMES.GREY;
      }
    }

    if (from) {
      const fromDateAsInt = dateToInt(from);

      if (to) {
        const toDateAsInt = dateToInt(to);

        // Handle dates between from and to dates
        if (dayAsInt >= fromDateAsInt && dayAsInt <= toDateAsInt) {
          return classColor === PRICE_COLOR_CLASS_NAMES.WHITE;
        }
      } else {
        if (dayAsInt === fromDateAsInt) {
          return classColor === PRICE_COLOR_CLASS_NAMES.WHITE;
        }

        // Handle dates before and after from date
        switch (classColor) {
          case PRICE_COLOR_CLASS_NAMES.WHITE:
            return false;

          case PRICE_COLOR_CLASS_NAMES.GREEN_OPACITY_45:
          case PRICE_COLOR_CLASS_NAMES.GREY:
          case PRICE_COLOR_CLASS_NAMES.ORANGE_OPACITY_45:
            if (dayAsInt >= fromDateAsInt) {
              return false;
            }

            break;

          default:
            if (dayAsInt < fromDateAsInt) {
              return false;
            }

            break;
        }
      }
    }

    // Get the correct color based on price
    if (resolvePrice) {
      const dayWithPrice = resolvePrice(dateToInt(day)) || {
        isBestPrice: false,
        lastAvailability: false,
        price: '',
      };
      const { isBestPrice, lastAvailability, price, siblingOpen } = dayWithPrice;

      if (siblingOpen) {
        return from
          ? classColor === PRICE_COLOR_CLASS_NAMES.TURQUOISE ||
              classColor === PRICE_COLOR_CLASS_NAMES.TURQUOISE
          : classColor === PRICE_COLOR_CLASS_NAMES.TURQUOISE;
      }

      if (lastAvailability) {
        return from
          ? classColor === PRICE_COLOR_CLASS_NAMES.ORANGE ||
              classColor === PRICE_COLOR_CLASS_NAMES.ORANGE_OPACITY_45
          : classColor === PRICE_COLOR_CLASS_NAMES.ORANGE;
      }

      if (isBestPrice) {
        return from
          ? classColor === PRICE_COLOR_CLASS_NAMES.GREEN ||
              classColor === PRICE_COLOR_CLASS_NAMES.GREEN_OPACITY_45
          : classColor === PRICE_COLOR_CLASS_NAMES.GREEN;
      }

      if (price) {
        return from
          ? classColor === PRICE_COLOR_CLASS_NAMES.DARK_GREY ||
              classColor === PRICE_COLOR_CLASS_NAMES.GREY
          : classColor === PRICE_COLOR_CLASS_NAMES.DARK_GREY;
      }
    }

    return false;
  };
}
export function flattenPrices(prices: PricesByDays) {
  const pricesIds = Object.keys(prices);
  return pricesIds.map((priceId) => prices[priceId].oldPrice).join();
}
